import { useState } from 'react';
import { Erfolgsmeldung } from '../../../gemeinsam/komponenten/Meldungen';
import { api, ApiFehler } from '../../../gemeinsam/lib/api';
import { useDaten, euro, datum } from '../../../gemeinsam/lib/daten';
import {
  Abzeichen, Auswahl, Feld, Hinweis, Karte, KartenKopf, Knopf, LeerZustand, Spinner, TarifSperre,
} from '../../../gemeinsam/komponenten/Basis';

type Kategorie = { id: string; name: string; euerZeile: number; skr03: string; abzugsfaehig?: number };
type Ausgabe = {
  id: string; kategorie: string; bezeichnung: string; lieferant: string; belegnummer: string;
  datum: string; zahlart: string; netto: number; vorsteuer: number; brutto: number;
  steuersatz: number; festgeschrieben: boolean; notiz: string;
  status?: 'offen' | 'bezahlt'; faelligAm?: string | null; bezahltAm?: string | null;
};
type Antwort = { ausgaben: Ausgabe[]; summen: { anzahl: number; brutto: number; netto: number; vorsteuer: number; offen?: number; offenAnzahl?: number } };

const LEER = {
  kategorie: 'sonstiges', bezeichnung: '', lieferant: '', belegnummer: '',
  brutto: '', steuersatz: '19', zahlart: 'ueberweisung',
  datum: new Date().toISOString().slice(0, 10), notiz: '',
  // Offen = Rechnung noch nicht bezahlt (Verbindlichkeit), mit Fälligkeit.
  status: 'bezahlt' as 'bezahlt' | 'offen',
  faelligAm: new Date(Date.now() + 14 * 86400000).toISOString().slice(0, 10),
};

/** `eingebettet`: als Reiter in der Buchhaltung — ohne eigene Seitenbreite und Hauptüberschrift. */
export default function Ausgaben({ eingebettet = false }: { eingebettet?: boolean }) {
  const [kategorie, setKategorie] = useState('');
  const [nurOffen, setNurOffen] = useState(false);
  const abfrage = [kategorie ? `kategorie=${kategorie}` : '', nurOffen ? 'status=offen' : ''].filter(Boolean).join('&');
  const { daten, laedt, fehler, tarifsperre, neuLaden } = useDaten<Antwort>(
    `/expenses${abfrage ? `?${abfrage}` : ''}`, [abfrage]);
  const { daten: kategorien } = useDaten<Kategorie[]>('/expenses/kategorien');

  const [formular, setFormular] = useState<typeof LEER | null>(null);
  const [sendeFehler, setSendeFehler] = useState<string | null>(null);
  const [meldung, setMeldung] = useState<string | null>(null);
  const [sendet, setSendet] = useState(false);

  if (tarifsperre) {
    return (
      <div className="mx-auto max-w-3xl space-y-4">
        <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100">Ausgaben</h1>
        <TarifSperre was="Die Buchhaltung" upgradeAuf={tarifsperre.upgradeAuf} />
      </div>
    );
  }

  async function alsBezahlt(a: Ausgabe) {
    setSendeFehler(null);
    try {
      await api(`/expenses/${a.id}/bezahlt`, { methode: 'POST', daten: {} });
      setMeldung(`„${a.bezeichnung}" als bezahlt markiert.`);
      await neuLaden();
    } catch (e) {
      setSendeFehler(e instanceof ApiFehler ? e.message : 'Das hat nicht geklappt.');
    }
  }

  async function speichern(e: React.FormEvent) {
    e.preventDefault();
    if (!formular) return;
    setSendeFehler(null);
    setSendet(true);
    try {
      await api('/expenses', {
        methode: 'POST',
        daten: {
          ...formular,
          brutto: Number(formular.brutto.replace(',', '.')) || 0,
          steuersatz: Number(formular.steuersatz),
          faelligAm: formular.status === 'offen' ? formular.faelligAm : undefined,
        },
      });
      setFormular(null);
      setMeldung('Ausgabe erfasst.');
      await neuLaden();
    } catch (e) {
      setSendeFehler(e instanceof ApiFehler ? e.message : 'Speichern fehlgeschlagen.');
    } finally {
      setSendet(false);
    }
  }

  async function festschreiben(a: Ausgabe) {
    if (!confirm(`„${a.bezeichnung}" festschreiben?\n\nDanach ist die Ausgabe unveränderlich — so verlangt es die GoBD.`)) return;
    try {
      await api(`/expenses/${a.id}/festschreiben`, { methode: 'POST' });
      setMeldung('Ausgabe festgeschrieben.');
      await neuLaden();
    } catch (e) {
      setSendeFehler(e instanceof ApiFehler ? e.message : 'Festschreiben fehlgeschlagen.');
    }
  }

  async function loeschen(a: Ausgabe) {
    if (!confirm(`„${a.bezeichnung}" löschen?`)) return;
    try {
      await api(`/expenses/${a.id}`, { methode: 'DELETE' });
      setMeldung('Ausgabe gelöscht.');
      await neuLaden();
    } catch (e) {
      setSendeFehler(e instanceof ApiFehler ? e.message : 'Löschen fehlgeschlagen.');
    }
  }

  const nameVon = (id: string) => kategorien?.find(k => k.id === id)?.name ?? id;

  return (
    <div className={eingebettet ? 'space-y-4' : 'mx-auto max-w-5xl space-y-4'}>
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          {eingebettet
            ? <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100">Ausgaben</h2>
            : <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100">Ausgaben</h1>}
          {daten && (
            <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">
              {daten.summen.anzahl} Belege · {euro(daten.summen.brutto)} brutto ·
              {' '}{euro(daten.summen.vorsteuer)} Vorsteuer
            </p>
          )}
        </div>
        {!formular && (
          <Knopf variante="primaer" onClick={() => { setFormular({ ...LEER }); setMeldung(null); }}>
            Ausgabe erfassen
          </Knopf>
        )}
      </div>

      {fehler && <Hinweis art="fehler">{fehler}</Hinweis>}
      {sendeFehler && <Hinweis art="fehler">{sendeFehler}</Hinweis>}
      <Erfolgsmeldung text={meldung} />

      {formular && (
        <Karte>
          <KartenKopf titel="Neue Ausgabe" hinweis="Betrag wie auf dem Beleg — brutto." />
          <form onSubmit={speichern} className="space-y-4 p-5">
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              <Auswahl label="Kategorie" value={formular.kategorie} required
                       onChange={e => setFormular(f => f && ({ ...f, kategorie: e.target.value }))}>
                {(kategorien ?? []).map(k => (
                  <option key={k.id} value={k.id}>{k.name}{k.abzugsfaehig ? ` (${k.abzugsfaehig * 100} %)` : ''}</option>
                ))}
              </Auswahl>
              <Feld label="Bezeichnung" value={formular.bezeichnung} required autoFocus
                    onChange={e => setFormular(f => f && ({ ...f, bezeichnung: e.target.value }))} />
              <Feld label="Zahlungsempfänger" value={formular.lieferant}
                    onChange={e => setFormular(f => f && ({ ...f, lieferant: e.target.value }))} />
              <Feld label="Bruttobetrag" value={formular.brutto} inputMode="decimal" required
                    hinweis="Netto und Vorsteuer werden daraus berechnet."
                    onChange={e => setFormular(f => f && ({ ...f, brutto: e.target.value }))} />
              <Auswahl label="Steuersatz" value={formular.steuersatz}
                       onChange={e => setFormular(f => f && ({ ...f, steuersatz: e.target.value }))}>
                <option value="19">19 %</option>
                <option value="7">7 %</option>
                <option value="0">0 % / keine Vorsteuer</option>
              </Auswahl>
              <Feld label="Datum" type="date" value={formular.datum}
                    onChange={e => setFormular(f => f && ({ ...f, datum: e.target.value }))} />
              <Feld label="Belegnummer" value={formular.belegnummer}
                    onChange={e => setFormular(f => f && ({ ...f, belegnummer: e.target.value }))} />
              <Auswahl label="Zahlart" value={formular.zahlart}
                       onChange={e => setFormular(f => f && ({ ...f, zahlart: e.target.value }))}>
                <option value="ueberweisung">Überweisung</option>
                <option value="lastschrift">Lastschrift</option>
                <option value="karte">Karte</option>
                <option value="bar">Bar</option>
                <option value="privat">Privat verauslagt</option>
              </Auswahl>
              <Feld label="Notiz" value={formular.notiz}
                    onChange={e => setFormular(f => f && ({ ...f, notiz: e.target.value }))} />
              <Auswahl label="Bezahlt?" value={formular.status}
                       onChange={e => setFormular(f => f && ({ ...f, status: e.target.value as 'offen' | 'bezahlt' }))}>
                <option value="bezahlt">Ja, bezahlt</option>
                <option value="offen">Nein — Rechnung noch offen</option>
              </Auswahl>
              {formular.status === 'offen' && (
                <Feld label="Fällig am" type="date" value={formular.faelligAm}
                      hinweis="Zählt erst bei Zahlung als Ausgabe; bis dahin in der Finanzlage eingeplant."
                      onChange={e => setFormular(f => f && ({ ...f, faelligAm: e.target.value }))} />
              )}
            </div>
            <div className="flex gap-2">
              <Knopf type="submit" variante="primaer" laedt={sendet}>Speichern</Knopf>
              <Knopf type="button" onClick={() => { setFormular(null); setSendeFehler(null); }}>Abbrechen</Knopf>
            </div>
          </form>
        </Karte>
      )}

      <Karte>
        <div className="border-b border-slate-200 p-4 dark:border-slate-800">
          <div className="flex flex-wrap items-end gap-4">
            <div className="min-w-[14rem] flex-1">
              <Auswahl label="Kategorie" value={kategorie} onChange={e => setKategorie(e.target.value)}>
                <option value="">Alle Kategorien</option>
                {(kategorien ?? []).map(k => <option key={k.id} value={k.id}>{k.name}</option>)}
              </Auswahl>
            </div>
            <label className="flex items-center gap-2 pb-2 text-sm text-slate-700 dark:text-slate-300">
              <input type="checkbox" checked={nurOffen} onChange={e => setNurOffen(e.target.checked)} />
              Nur offene Rechnungen{daten?.summen.offenAnzahl ? ` (${daten.summen.offenAnzahl} · ${euro(daten.summen.offen ?? 0)})` : ''}
            </label>
          </div>
        </div>

        {laedt && !daten ? (
          <div className="flex justify-center py-12"><Spinner /></div>
        ) : !daten?.ausgaben.length ? (
          <LeerZustand titel="Noch keine Ausgaben"
                       text="Erfassen Sie Miete, Material, Versicherungen — alles, was das Ergebnis mindert." />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="border-b border-slate-200 text-left text-xs uppercase text-slate-500 dark:border-slate-800 dark:text-slate-400">
                <tr>
                  <th className="px-5 py-2.5 font-medium">Beleg</th>
                  <th className="px-3 py-2.5 font-medium">Kategorie</th>
                  <th className="px-3 py-2.5 text-right font-medium">Netto</th>
                  <th className="px-3 py-2.5 text-right font-medium">Vorsteuer</th>
                  <th className="px-3 py-2.5 text-right font-medium">Brutto</th>
                  <th className="px-5 py-2.5 text-right font-medium">Aktion</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200 dark:divide-slate-800">
                {daten.ausgaben.map(a => (
                  <tr key={a.id}>
                    <td className="px-5 py-3">
                      <p className="font-medium text-slate-900 dark:text-slate-100">{a.bezeichnung}</p>
                      <p className="text-xs text-slate-500 dark:text-slate-400">
                        {datum(a.datum)}{a.lieferant ? ` · ${a.lieferant}` : ''}{a.belegnummer ? ` · ${a.belegnummer}` : ''}
                      </p>
                      {a.status === 'offen' && (
                        <span className="mt-1 inline-block">
                          <Abzeichen art={a.faelligAm && a.faelligAm.slice(0, 10) < new Date().toISOString().slice(0, 10) ? 'rot' : 'gelb'}>
                            offen · fällig {datum(a.faelligAm)}
                          </Abzeichen>
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-3 text-slate-600 dark:text-slate-400">{nameVon(a.kategorie)}</td>
                    <td className="px-3 py-3 text-right text-slate-600 dark:text-slate-400">{euro(a.netto)}</td>
                    <td className="px-3 py-3 text-right text-slate-600 dark:text-slate-400">{euro(a.vorsteuer)}</td>
                    <td className="px-3 py-3 text-right font-medium text-slate-900 dark:text-slate-100">{euro(a.brutto)}</td>
                    <td className="px-5 py-3">
                      <div className="flex items-center justify-end gap-1.5">
                        {a.status === 'offen' && (
                          <Knopf variante="primaer" className="!px-2 !py-1 !text-xs" onClick={() => alsBezahlt(a)}>Bezahlt</Knopf>
                        )}
                        {a.festgeschrieben ? (
                          <Abzeichen art="gruen">festgeschrieben</Abzeichen>
                        ) : (
                          <>
                            <Knopf variante="still" className="!px-2 !py-1 !text-xs" onClick={() => festschreiben(a)}>
                              Festschreiben
                            </Knopf>
                            <Knopf variante="still" className="!px-2 !py-1 !text-xs" onClick={() => loeschen(a)}>
                              Löschen
                            </Knopf>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Karte>
    </div>
  );
}
